/**
 * Club Owner Dashboard - Main landing page for club owners
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Building2, Radio, Users, MapPin, TrendingUp,
  ShoppingCart, Settings, ChevronRight, Loader2,
  Package, DollarSign, BarChart3, Activity, Zap, Calendar, Play, MessageCircle, Camera
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import ClubNavbar from '../../components/ClubNavbar';
import { useAuthStore } from '../../store';
import api, { publicAPI } from '../../services/api';
import { toast } from 'sonner';

// Club API endpoints
const clubAPI = {
  getButtons: () => api.get('/club/buttons'),
  getCourts: () => api.get('/club/courts'),
  getStats: () => api.get('/club/stats'),
  getOrders: () => api.get('/club/orders'),
  getButtonStats: (period) => api.get(`/club/button-stats?period=${period}`),
};

// Stat card component
const StatCard = ({ icon: Icon, label, value, subValue, iconColor = "text-primary", bgColor = "bg-primary/10" }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="glass rounded-xl p-4"
  >
    <div className="flex items-start gap-3">
      <div className={`w-10 h-10 rounded-lg ${bgColor} flex items-center justify-center ${iconColor}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-2xl font-bold font-display">{value}</p>
        <p className="text-sm text-muted-foreground">{label}</p>
        {subValue && (
          <p className="text-xs text-muted-foreground mt-1">{subValue}</p>
        )}
      </div>
    </div>
  </motion.div>
);

// Quick action card component
const QuickAction = ({ icon: Icon, title, description, onClick, variant = "default" }) => (
  <motion.button
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className={`p-4 rounded-xl text-left transition-all w-full ${
      variant === 'primary'
        ? 'bg-primary text-primary-foreground hover:bg-primary/90'
        : 'bg-secondary/50 hover:bg-secondary/80'
    }`}
  >
    <Icon className="w-6 h-6 mb-2" />
    <p className="font-medium">{title}</p>
    <p className={`text-xs ${variant === 'primary' ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
      {description}
    </p>
  </motion.button>
);

export default function ClubDashboard() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  
  // Check if user is club owner or admin
  const isClubOwner = user?.role === 'club_owner' || user?.role === 'admin';
  
  // State
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalReservations: 0,
    reservationRevenue: 0,
    posSales: 0,
    posRevenue: 0,
    totalCourts: 0,
    activeSessions: 0,
  });
  const [buttons, setButtons] = useState([]);
  const [courts, setCourts] = useState([]);
  const [activePlayers, setActivePlayers] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  
  // WhatsApp support
  const [supportWhatsapp, setSupportWhatsapp] = useState(null);
  
  // Button usage stats
  const [buttonUsageStats, setButtonUsageStats] = useState(null);
  const [usagePeriod, setUsagePeriod] = useState('week');
  const [loadingUsageStats, setLoadingUsageStats] = useState(false);
  
  // Load WhatsApp support number
  useEffect(() => {
    const loadSupportNumber = async () => {
      try {
        const response = await publicAPI.getSupportWhatsapp();
        setSupportWhatsapp(response.data);
      } catch (err) {
        console.error('Error loading support number:', err);
      }
    };
    loadSupportNumber();
  }, []);
  
  // Handle WhatsApp contact
  const handleContactSupport = () => {
    if (!supportWhatsapp?.whatsapp) {
      toast.error('Número de soporte no configurado');
      return;
    }
    
    // Format phone number (remove + and spaces)
    const phone = supportWhatsapp.whatsapp.replace(/[^0-9]/g, '');
    const message = encodeURIComponent(supportWhatsapp.default_message || 'Hola, necesito ayuda');
    
    // Open WhatsApp - works on both mobile and desktop
    const whatsappUrl = `https://wa.me/${phone}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };
  
  // Load button usage stats
  const loadButtonUsageStats = useCallback(async (period) => {
    try {
      setLoadingUsageStats(true);
      const response = await clubAPI.getButtonStats(period);
      setButtonUsageStats(response.data);
    } catch (err) {
      console.error('Error loading button stats:', err);
    } finally {
      setLoadingUsageStats(false);
    }
  }, []);
  
  // Handle period change
  const handlePeriodChange = (period) => {
    setUsagePeriod(period);
    loadButtonUsageStats(period);
  };
  
  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [buttonsRes, courtsRes, playersRes, orderHistoryRes] = await Promise.all([
        clubAPI.getButtons().catch(() => ({ data: { buttons: [] } })),
        clubAPI.getCourts().catch(() => ({ data: { courts: [] } })),
        api.get('/club/active-players').catch(() => ({ data: { players: [] } })),
        api.get('/club-owner/order-history?period=month&source=all').catch(() => ({ data: { orders: [], summary: {} } })),
      ]);
      
      const buttonsData = buttonsRes.data.buttons || [];
      const courtsData = courtsRes.data.courts || [];
      const playersData = playersRes.data.players || [];
      const orderSummary = orderHistoryRes.data.summary || {};
      
      setButtons(buttonsData);
      setCourts(courtsData);
      setActivePlayers(playersData);
      
      // Get reservations count
      let reservationCount = 0;
      let reservationRevenue = 0;
      try {
        const resRes = await api.get('/club-owner/reservations?period=month');
        reservationCount = resRes.data.total || 0;
        reservationRevenue = resRes.data.revenue || 0;
      } catch (e) {
        console.log('Reservations endpoint not available');
      }
      
      // Calculate stats - Reservaciones/POS
      setStats({
        totalReservations: reservationCount,
        reservationRevenue: reservationRevenue,
        posSales: orderSummary.pos_sales || 0,
        posRevenue: orderSummary.revenue_pos || 0,
        totalCourts: courtsData.length,
        activeSessions: playersData.length,
      });
      
      // Recent activity
      const activities = [];
      
      // Add recent sales from order history
      const recentOrders = orderHistoryRes.data.orders || [];
      recentOrders.slice(0, 3).forEach(o => {
        activities.push({
          type: o.source === 'pos' ? 'pos' : 'reservation',
          message: o.source === 'pos' 
            ? `Venta POS: $${o.total?.toLocaleString() || 0}` 
            : `Reservación: ${o.customer_name || 'Cliente'}`,
          time: o.source === 'pos' ? 'Punto de Venta' : 'Reservación'
        });
      });
      
      // Add session activities
      if (playersData.length > 0) {
        activities.push({
          type: 'session',
          message: `${playersData.length} jugador(es) activo(s) ahora`,
          time: 'En vivo'
        });
      }
      
      setRecentActivity(activities.slice(0, 5));
      
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      toast.error('Error al cargar datos del dashboard');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    
    if (!isClubOwner) {
      navigate('/home');
      return;
    }
    
    loadData();
    loadButtonUsageStats(usagePeriod);
  }, [isAuthenticated, isClubOwner, navigate, loadData, loadButtonUsageStats, usagePeriod]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <ClubNavbar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background pb-24">
      <ClubNavbar />
      
      <div className="max-w-6xl mx-auto p-4">
        {/* Welcome section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-center gap-3 mb-1">
            <Building2 className="w-8 h-8 text-primary" />
            <h2 className="font-display font-bold text-2xl md:text-3xl">
              ¡Hola{user?.first_name ? `, ${user.first_name}` : ''}! 👋
            </h2>
          </div>
          <p className="text-muted-foreground ml-11">
            Panel de administración de tu club
          </p>
        </motion.div>
        
        {/* Stats grid - Reservaciones/POS (no tienda) */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard
            icon={Calendar}
            label="Reservaciones"
            value={stats.totalReservations || 0}
            subValue="Este mes"
            iconColor="text-blue-400"
            bgColor="bg-blue-400/10"
          />
          
          <StatCard
            icon={DollarSign}
            label="Ingresos Reservas"
            value={`$${(stats.reservationRevenue || 0).toLocaleString()}`}
            subValue="MXN este mes"
            iconColor="text-green-400"
            bgColor="bg-green-400/10"
          />
          
          <StatCard
            icon={Package}
            label="Ventas POS"
            value={stats.posSales || 0}
            subValue="Este mes"
            iconColor="text-purple-400"
            bgColor="bg-purple-400/10"
          />
          
          <StatCard
            icon={TrendingUp}
            label="Ingresos POS"
            value={`$${(stats.posRevenue || 0).toLocaleString()}`}
            subValue="MXN este mes"
            iconColor="text-yellow-400"
            bgColor="bg-yellow-400/10"
          />
        </div>
        
        {/* Quick Actions - Solo 4 botones */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <h3 className="font-display font-bold text-lg mb-4">Acciones rápidas</h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <QuickAction
              icon={DollarSign}
              title="Punto de Venta"
              description="Ventas y cobros"
              onClick={() => navigate('/pos')}
              variant="primary"
            />
            
            <QuickAction
              icon={Calendar}
              title="Reservaciones"
              description="Gestión de canchas"
              onClick={() => navigate('/club/reservations')}
            />
            
            <QuickAction
              icon={Camera}
              title="Cámaras"
              description="Gestión por cancha"
              onClick={() => navigate('/club/cameras')}
            />
            
            <QuickAction
              icon={BarChart3}
              title="Estadísticas"
              description="Reportes y métricas"
              onClick={() => navigate('/club/stats')}
              variant="primary"
            />
          </div>
        </motion.div>
        
        {/* Button Usage Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-6"
        >
          <Card className="bg-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-500" />
                  Uso de Botones por Cancha
                </CardTitle>
                
                {/* Period Filter */}
                <div className="flex items-center gap-1 bg-secondary/50 rounded-lg p-1">
                  {[
                    { value: 'day', label: 'Día' },
                    { value: 'week', label: 'Semana' },
                    { value: 'month', label: 'Mes' },
                    { value: 'year', label: 'Año' },
                  ].map((period) => (
                    <button
                      key={period.value}
                      onClick={() => handlePeriodChange(period.value)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        usagePeriod === period.value
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-secondary text-muted-foreground'
                      }`}
                    >
                      {period.label}
                    </button>
                  ))}
                </div>
              </div>
              <CardDescription>
                Estadísticas de clips generados con botones de renta
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingUsageStats ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : buttonUsageStats?.court_stats?.length > 0 ? (
                <div className="space-y-4">
                  {/* Totals Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-yellow-500/10 rounded-lg p-3 text-center">
                      <Zap className="w-5 h-5 mx-auto mb-1 text-yellow-500" />
                      <p className="text-xl font-bold">{buttonUsageStats.totals?.total_button_presses || 0}</p>
                      <p className="text-xs text-muted-foreground">Usos totales</p>
                    </div>
                    <div className="bg-blue-500/10 rounded-lg p-3 text-center">
                      <Radio className="w-5 h-5 mx-auto mb-1 text-blue-500" />
                      <p className="text-xl font-bold">{buttonUsageStats.totals?.total_clips_generated || 0}</p>
                      <p className="text-xs text-muted-foreground">Clips generados</p>
                    </div>
                    <div className="bg-green-500/10 rounded-lg p-3 text-center">
                      <MapPin className="w-5 h-5 mx-auto mb-1 text-green-500" />
                      <p className="text-xl font-bold">{buttonUsageStats.totals?.total_courts || 0}</p>
                      <p className="text-xs text-muted-foreground">Canchas</p>
                    </div>
                    <div className="bg-purple-500/10 rounded-lg p-3 text-center">
                      <Calendar className="w-5 h-5 mx-auto mb-1 text-purple-500" />
                      <p className="text-xl font-bold">{usagePeriod === 'day' ? '24h' : usagePeriod === 'week' ? '7d' : usagePeriod === 'month' ? '30d' : '1a'}</p>
                      <p className="text-xs text-muted-foreground">Período</p>
                    </div>
                  </div>
                  
                  {/* Usage by Court */}
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm text-muted-foreground">Uso por cancha</h4>
                    {buttonUsageStats.court_stats.map((court, index) => (
                      <div 
                        key={court.court_id || index}
                        className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            court.button_presses > 0 ? 'bg-yellow-500/20' : 'bg-secondary'
                          }`}>
                            <span className={`text-sm font-bold ${
                              court.button_presses > 0 ? 'text-yellow-500' : 'text-muted-foreground'
                            }`}>
                              {court.court_number || court.number || (index + 1)}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-sm">{court.court_name || court.name || `Cancha ${court.court_number || court.number || (index + 1)}`}</p>
                            <p className="text-xs text-muted-foreground">{court.club_name}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-yellow-500">{court.button_presses}</p>
                          <p className="text-xs text-muted-foreground">
                            {court.button_presses === 1 ? 'uso' : 'usos'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Zap className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground mb-1">No hay datos de uso</p>
                  <p className="text-xs text-muted-foreground">
                    Los clips generados con botones de renta aparecerán aquí
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
        
        {/* Two column layout */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Buttons Overview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="bg-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Radio className="w-5 h-5 text-primary" />
                    Estado de Botones
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/club/buttons')}>
                    Ver todos
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-blue-500/10 rounded-lg">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-blue-500" />
                      <span>Para Renta</span>
                    </div>
                    <span className="font-bold text-blue-500">{stats.rentalButtons}</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-green-500" />
                      <span>Para Reventa</span>
                    </div>
                    <span className="font-bold text-green-500">{stats.resaleButtons}</span>
                  </div>
                  
                  {stats.unconfiguredButtons > 0 && (
                    <div className="flex items-center justify-between p-3 bg-yellow-500/10 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Settings className="w-4 h-4 text-yellow-500" />
                        <span>Sin Configurar</span>
                      </div>
                      <span className="font-bold text-yellow-500">{stats.unconfiguredButtons}</span>
                    </div>
                  )}
                </div>
                
                {stats.totalButtons === 0 && (
                  <div className="text-center py-6">
                    <Package className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground mb-3">No tienes botones aún</p>
                    <Button size="sm" onClick={() => navigate('/club/buttons')}>
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      Comprar Botones
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
          
          {/* Courts Overview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            <Card className="bg-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-blue-400" />
                  Estadísticas de Canchas
                </CardTitle>
              </CardHeader>
              <CardContent>
                {courts.length > 0 ? (
                  <div className="space-y-3">
                    {courts.slice(0, 4).map((court, index) => (
                      <div key={court.court_id || index} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                            <span className="text-sm font-bold text-blue-400">{court.court_number || court.number || (index + 1)}</span>
                          </div>
                          <div>
                            <p className="text-sm font-medium">{court.court_name || court.name || `Cancha ${court.court_number || court.number || (index + 1)}`}</p>
                            <p className="text-xs text-muted-foreground">{court.club_name || 'Mi Club'}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            court.has_button ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {court.has_button ? 'Con botón' : 'Sin botón'}
                          </span>
                        </div>
                      </div>
                    ))}
                    {courts.length > 4 && (
                      <p className="text-xs text-muted-foreground text-center">
                        +{courts.length - 4} canchas más
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <MapPin className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">No hay canchas registradas</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
        
        {/* Active Players Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="bg-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-green-400" />
                  Jugadores Activos en Canchas
                  {activePlayers.length > 0 && (
                    <span className="ml-2 px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full animate-pulse">
                      {activePlayers.length} en vivo
                    </span>
                  )}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {activePlayers.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {activePlayers.map((player, index) => (
                    <div key={player.user_id || index} className="flex items-center gap-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                      <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                        <Users className="w-5 h-5 text-green-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{player.user_name || 'Jugador'}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {player.court_name || `Cancha ${player.court_number || player.court?.number || '?'}`}
                        </p>
                      </div>
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">No hay jugadores activos en este momento</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Los jugadores aparecerán aquí cuando escaneen el QR de una cancha
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
        >
          <Card className="bg-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-primary" />
                Actividad Reciente
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recentActivity.length > 0 ? (
                <div className="space-y-3">
                  {recentActivity.map((activity, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-secondary/30 rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        {activity.type === 'button' && <Radio className="w-4 h-4 text-primary" />}
                        {activity.type === 'order' && <ShoppingCart className="w-4 h-4 text-green-500" />}
                        {activity.type === 'session' && <Users className="w-4 h-4 text-blue-500" />}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{activity.message}</p>
                        <p className="text-xs text-muted-foreground">{activity.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <Activity className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">No hay actividad reciente</p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
        
        {/* Help Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-6"
        >
          <Card className="bg-gradient-to-r from-primary/10 to-blue-500/10 border-primary/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-display font-bold">¿Necesitas ayuda?</h4>
                  <p className="text-sm text-muted-foreground">Contacta a soporte técnico</p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleContactSupport}
                  className="gap-2"
                >
                  <MessageCircle className="w-4 h-4" />
                  Contactar
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
